<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Avatar from 'next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ContactAPI from 'dashboard/api/contacts';
import FunnelAPI from 'dashboard/api/funnels';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();

const funnelId = computed(() => Number(route.params.funnelId));
const cardId = computed(() => Number(route.params.cardId));

const card = useMapGetter('funnels/getCurrentCard');
const activities = useMapGetter('funnels/getActivities');
const uiFlags = useMapGetter('funnels/getUIFlags');

const activityType = ref('note');
const activityContent = ref('');
const selectedFile = ref(null);
const fileInputRef = ref(null);
const showLossModal = ref(false);
const lossReason = ref('');
const loadError = ref(false);
const editingActivityId = ref(null);
const editingContent = ref('');
const activeTab = ref('note');
const currentUser = useMapGetter('getCurrentUser');

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const contactConversation = ref(null);
const accountId = computed(() => store.getters.getCurrentAccountId);

const fetchContactConversation = async contactId => {
  if (!contactId) {
    contactConversation.value = null;
    return;
  }
  try {
    const { data } = await ContactAPI.getConversations(contactId);
    const conversations = data?.payload || [];
    contactConversation.value =
      conversations.length > 0 ? conversations[0] : null;
  } catch {
    contactConversation.value = null;
  }
};

const contactConversationUrl = computed(() => {
  if (!contactConversation.value) return null;
  return frontendURL(
    conversationUrl({
      accountId: accountId.value,
      id: contactConversation.value.id,
    })
  );
});

watch(
  () => card.value?.contact?.id,
  newContactId => fetchContactConversation(newContactId),
  { immediate: true }
);

const activityTypes = [
  {
    value: 'note',
    labelKey: 'CRM.DETAIL.ACTIVITY_NOTE',
    icon: 'i-lucide-sticky-note',
    activeColor: '#3b82f6',
  },
  {
    value: 'call',
    labelKey: 'CRM.DETAIL.ACTIVITY_CALL',
    icon: 'i-lucide-phone',
    activeColor: '#16a34a',
  },
  {
    value: 'meeting',
    labelKey: 'CRM.DETAIL.ACTIVITY_MEETING',
    icon: 'i-lucide-users',
    activeColor: '#7c3aed',
  },
  {
    value: 'task',
    labelKey: 'CRM.DETAIL.ACTIVITY_TASK',
    icon: 'i-lucide-list-checks',
    activeColor: '#d97706',
  },
  {
    value: 'file',
    labelKey: 'CRM.DETAIL.ACTIVITY_FILE',
    icon: 'i-lucide-paperclip',
    activeColor: '#6366f1',
  },
];

const stageActivities = computed(() =>
  activities.value.filter(a =>
    ['stage_change', 'status_change'].includes(a.activity_type)
  )
);

const userActivities = computed(() =>
  activities.value.filter(
    a => !['stage_change', 'status_change'].includes(a.activity_type)
  )
);

const activityCountByType = computed(() => {
  const counts = {};
  activityTypes.forEach(aType => {
    counts[aType.value] = userActivities.value.filter(
      a => a.activity_type === aType.value
    ).length;
  });
  return counts;
});

// Nome de exibição dos arquivos: usa o tipo de documento escolhido pelo agente
// (meta.document_name, ex: rg, certidao_de_casamento, extrato_bancario_nubank).
// Anexos antigos sem esse nome caem no alias por índice (doc1, doc2...).
// A extensão real é preservada (inferida do filename/content_type).
const fileDisplayNames = computed(() => {
  const fileActivities = userActivities.value
    .filter(a => a.activity_type === 'file' && a.file)
    .slice()
    .sort(
      (a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );
  const map = {};
  fileActivities.forEach((activity, idx) => {
    const extFromName = activity.file?.filename?.match(/\.([a-z0-9]+)$/i)?.[1];
    const extFromType = activity.file?.content_type?.split('/')?.[1];
    const ext = (extFromName || extFromType || '').toLowerCase();
    const base = activity.meta?.document_name || `doc${idx + 1}`;
    map[activity.id] = ext ? `${base}.${ext}` : base;
  });
  return map;
});

const displayFileName = activity =>
  fileDisplayNames.value[activity.id] || activity.file?.filename || '';

// -------- Unir PDF (merge de imagens + PDFs em um único PDF) --------
const isMergingPdf = ref(false);

// Activities do tipo file que são PDF ou imagem (ordenadas por data de criação)
const mergeableFileActivities = computed(() =>
  userActivities.value
    .filter(a => a.activity_type === 'file' && a.file)
    .filter(a => {
      const ct = (a.file?.content_type || '').toLowerCase();
      const fn = (a.file?.filename || '').toLowerCase();
      return (
        ct.startsWith('image/') ||
        ct === 'application/pdf' ||
        /\.(jpe?g|png|webp|pdf)$/.test(fn)
      );
    })
    .slice()
    .sort(
      (a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    )
);

const hasMergeableFiles = computed(
  () => mergeableFileActivities.value.length > 0
);

// -------- Modal de seleção de arquivos para unir --------
const showMergeModal = ref(false);
const selectedMergeIds = ref([]);

const openMergeModal = () => {
  if (mergeableFileActivities.value.length === 0) return;
  // Começa com todos marcados.
  selectedMergeIds.value = mergeableFileActivities.value.map(a => a.id);
  showMergeModal.value = true;
};

// Checkbox "Todos": marcado marca tudo, desmarcado desmarca tudo.
const allMergeSelected = computed({
  get() {
    return (
      mergeableFileActivities.value.length > 0 &&
      selectedMergeIds.value.length === mergeableFileActivities.value.length
    );
  },
  set(checked) {
    selectedMergeIds.value = checked
      ? mergeableFileActivities.value.map(a => a.id)
      : [];
  },
});

const sanitizeFileName = name =>
  (name || 'contato')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // remove acentos
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '') || 'contato';

const mergedPdfFileName = computed(() => {
  const contactName = card.value?.contact?.name || 'contato';
  return `Documentacao_${sanitizeFileName(contactName)}.pdf`;
});

const triggerBlobDownload = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

// A fusão acontece no servidor (poppler pdfunite + ImageMagick): baixa os
// arquivos escolhidos, converte imagens em páginas e junta tudo num único PDF.
// Substitui a versão no navegador (pdf-lib/pdf.js) que gerava páginas em branco
// em PDFs iText/gov (CTPS Digital, IRPF).
const confirmMergeFilesToPdf = async () => {
  if (isMergingPdf.value || selectedMergeIds.value.length === 0) return;
  isMergingPdf.value = true;
  try {
    const { data } = await FunnelAPI.mergeFilesToPdf(
      funnelId.value,
      cardId.value,
      selectedMergeIds.value
    );
    triggerBlobDownload(
      new Blob([data], { type: 'application/pdf' }),
      mergedPdfFileName.value
    );
    showMergeModal.value = false;
    useAlert(t('CRM.DETAIL.MERGE_PDF_SUCCESS'));
  } catch (e) {
    useAlert(t('CRM.DETAIL.MERGE_PDF_ERROR'));
  } finally {
    isMergingPdf.value = false;
  }
};

const activeTabItems = computed(() =>
  userActivities.value.filter(a => a.activity_type === activeTab.value)
);

const activityIconMap = {
  note: {
    icon: 'i-lucide-sticky-note',
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    text: 'text-blue-600 dark:text-blue-400',
  },
  call: {
    icon: 'i-lucide-phone',
    bg: 'bg-green-100 dark:bg-green-900/30',
    text: 'text-green-600 dark:text-green-400',
  },
  meeting: {
    icon: 'i-lucide-users',
    bg: 'bg-violet-100 dark:bg-violet-900/30',
    text: 'text-violet-600 dark:text-violet-400',
  },
  task: {
    icon: 'i-lucide-list-checks',
    bg: 'bg-amber-100 dark:bg-amber-900/30',
    text: 'text-amber-600 dark:text-amber-400',
  },
  file: {
    icon: 'i-lucide-paperclip',
    bg: 'bg-indigo-100 dark:bg-indigo-900/30',
    text: 'text-indigo-600 dark:text-indigo-400',
  },
  stage_change: {
    icon: 'i-lucide-arrow-right-left',
    bg: 'bg-orange-100 dark:bg-orange-900/30',
    text: 'text-orange-600 dark:text-orange-400',
  },
  status_change: {
    icon: 'i-lucide-flag',
    bg: 'bg-red-100 dark:bg-red-900/30',
    text: 'text-red-600 dark:text-red-400',
  },
};

const formattedValue = computed(() => {
  if (!card.value?.value || Number(card.value.value) === 0)
    return t('CRM.DETAIL.NO_VALUE');
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(card.value.value);
});

const formattedDate = dateStr => {
  if (!dateStr) return t('CRM.DETAIL.NOT_DEFINED');
  return new Date(dateStr).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const relativeTime = dateStr => {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return t('CRM.DETAIL.TODAY');
  if (days === 1) return t('CRM.DETAIL.AGO_1_DAY');
  return t('CRM.DETAIL.AGO_DAYS', { count: days });
};

const daysInStageColor = computed(() => {
  const days = card.value?.days_in_stage || 0;
  if (days <= 3) return 'text-green-600 dark:text-green-400';
  if (days <= 7) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
});

const statusBadge = computed(() => {
  switch (card.value?.status) {
    case 'won':
      return {
        label: t('CRM.DETAIL.STATUS_WON'),
        class:
          'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      };
    case 'lost':
      return {
        label: t('CRM.DETAIL.STATUS_LOST'),
        class: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
      };
    default:
      return {
        label: t('CRM.DETAIL.STATUS_OPEN'),
        class:
          'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      };
  }
});

const funnelStages = ref([]);
const isChangingStage = ref(false);

const fetchFunnelStages = async () => {
  try {
    const { data } = await FunnelAPI.show(funnelId.value);
    funnelStages.value = (data?.funnel_stages || [])
      .slice()
      .sort((a, b) => (a.position || 0) - (b.position || 0));
  } catch {
    funnelStages.value = [];
  }
};

const loadData = async () => {
  loadError.value = false;
  try {
    await Promise.all([
      store.dispatch('funnels/showCard', {
        funnelId: funnelId.value,
        cardId: cardId.value,
      }),
      store.dispatch('funnels/getActivities', {
        funnelId: funnelId.value,
        cardId: cardId.value,
      }),
      fetchFunnelStages(),
    ]);
  } catch {
    loadError.value = true;
  }
};

const handleStageChange = async event => {
  const newStageId = Number(event.target.value);
  if (
    !newStageId ||
    !card.value ||
    newStageId === card.value.funnel_stage_id ||
    isChangingStage.value
  ) {
    event.target.value = String(card.value?.funnel_stage_id || '');
    return;
  }
  isChangingStage.value = true;
  try {
    await store.dispatch('funnels/moveCard', {
      funnelId: funnelId.value,
      cardId: cardId.value,
      funnelStageId: newStageId,
      position: 0,
    });
    // Recarrega activities para mostrar o registro de "Movido de X para Y"
    await store.dispatch('funnels/getActivities', {
      funnelId: funnelId.value,
      cardId: cardId.value,
    });
    useAlert(t('CRM.DETAIL.STAGE_CHANGE_SUCCESS'));
  } catch {
    useAlert(t('CRM.DETAIL.STAGE_CHANGE_ERROR'));
    event.target.value = String(card.value?.funnel_stage_id || '');
  } finally {
    isChangingStage.value = false;
  }
};

onMounted(loadData);

const goBack = () => {
  router.push({
    name: 'crm_dashboard',
    query: { funnel_id: funnelId.value },
  });
};

const clearFile = () => {
  selectedFile.value = null;
  if (fileInputRef.value) fileInputRef.value.value = '';
};

const onFileSelect = event => {
  const file = event.target.files?.[0];
  if (!file) return;
  if (file.size > MAX_FILE_SIZE) {
    useAlert(t('CRM.DETAIL.ACTIVITY_FILE_TOO_LARGE'));
    event.target.value = '';
    return;
  }
  selectedFile.value = file;
};

const formatFileSize = bytes => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const submitActivity = async () => {
  if (activityType.value === 'file') {
    if (!selectedFile.value) return;
    const formData = new FormData();
    formData.append('funnel_card_activity[activity_type]', 'file');
    formData.append(
      'funnel_card_activity[content]',
      activityContent.value || selectedFile.value.name
    );
    formData.append('funnel_card_activity[file]', selectedFile.value);
    try {
      await store.dispatch('funnels/createActivity', {
        funnelId: funnelId.value,
        cardId: cardId.value,
        formData,
      });
      activityContent.value = '';
      clearFile();
      useAlert(t('CRM.DETAIL.ACTIVITY_SAVED'));
    } catch {
      useAlert(t('CRM.DETAIL.ACTIVITY_ERROR'));
    }
    return;
  }
  if (!activityContent.value.trim()) return;
  try {
    await store.dispatch('funnels/createActivity', {
      funnelId: funnelId.value,
      cardId: cardId.value,
      funnel_card_activity: {
        activity_type: activityType.value,
        content: activityContent.value,
      },
    });
    activityContent.value = '';
    useAlert(t('CRM.DETAIL.ACTIVITY_SAVED'));
  } catch {
    useAlert(t('CRM.DETAIL.ACTIVITY_ERROR'));
  }
};

const startEditActivity = activity => {
  editingActivityId.value = activity.id;
  editingContent.value = activity.content || '';
};

const cancelEditActivity = () => {
  editingActivityId.value = null;
  editingContent.value = '';
};

const saveEditActivity = async activity => {
  if (!editingContent.value.trim()) return;
  try {
    await store.dispatch('funnels/updateActivity', {
      funnelId: funnelId.value,
      cardId: cardId.value,
      activityId: activity.id,
      content: editingContent.value,
    });
    editingActivityId.value = null;
    editingContent.value = '';
    useAlert(t('CRM.DETAIL.ACTIVITY_UPDATED'));
  } catch {
    useAlert(t('CRM.DETAIL.ACTIVITY_ERROR'));
  }
};

const deleteActivity = async activity => {
  try {
    await store.dispatch('funnels/deleteActivity', {
      funnelId: funnelId.value,
      cardId: cardId.value,
      activityId: activity.id,
    });
    useAlert(t('CRM.DETAIL.ACTIVITY_DELETED'));
  } catch {
    useAlert(t('CRM.DETAIL.ACTIVITY_DELETE_ERROR'));
  }
};

const isEditableActivity = activity => {
  return (
    !['stage_change', 'status_change'].includes(activity.activity_type) &&
    activity.created_by === currentUser.value?.id
  );
};

const markWon = async () => {
  try {
    await store.dispatch('funnels/markCardWon', {
      funnelId: funnelId.value,
      cardId: cardId.value,
    });
    await store.dispatch('funnels/getActivities', {
      funnelId: funnelId.value,
      cardId: cardId.value,
    });
    useAlert(t('CRM.DETAIL.WON_SUCCESS'));
  } catch {
    useAlert(t('CRM.DETAIL.STATUS_ERROR'));
  }
};

const markLost = async () => {
  try {
    await store.dispatch('funnels/markCardLost', {
      funnelId: funnelId.value,
      cardId: cardId.value,
      lossReason: lossReason.value,
    });
    await store.dispatch('funnels/getActivities', {
      funnelId: funnelId.value,
      cardId: cardId.value,
    });
    showLossModal.value = false;
    lossReason.value = '';
    useAlert(t('CRM.DETAIL.LOST_SUCCESS'));
  } catch {
    useAlert(t('CRM.DETAIL.STATUS_ERROR'));
  }
};

// --- Migrate card to another funnel ---
const allFunnels = useMapGetter('funnels/getFunnels');
const showMigrateModal = ref(false);
const migrateTargetFunnelId = ref(null);
const migrateTargetStageId = ref(null);
const migrateTargetStages = ref([]);
const isMigrating = ref(false);
const isLoadingMigrateStages = ref(false);

const eligibleFunnels = computed(() =>
  (allFunnels.value || []).filter(f => f.id !== funnelId.value)
);

const openMigrateModal = async () => {
  if (!allFunnels.value || allFunnels.value.length === 0) {
    await store.dispatch('funnels/get');
  }
  migrateTargetFunnelId.value = null;
  migrateTargetStageId.value = null;
  migrateTargetStages.value = [];
  showMigrateModal.value = true;
};

watch(migrateTargetFunnelId, async newId => {
  migrateTargetStageId.value = null;
  migrateTargetStages.value = [];
  if (!newId) return;
  isLoadingMigrateStages.value = true;
  try {
    const { data } = await FunnelAPI.show(newId);
    migrateTargetStages.value = (data?.funnel_stages || [])
      .slice()
      .sort((a, b) => a.position - b.position);
    if (migrateTargetStages.value.length > 0) {
      migrateTargetStageId.value = migrateTargetStages.value[0].id;
    }
  } catch {
    useAlert(t('CRM.DETAIL.MIGRATE_STAGES_ERROR'));
  } finally {
    isLoadingMigrateStages.value = false;
  }
});

const performMigrate = async () => {
  if (!migrateTargetFunnelId.value || !migrateTargetStageId.value) return;
  isMigrating.value = true;
  try {
    const { data } = await FunnelAPI.migrateCard(funnelId.value, cardId.value, {
      targetFunnelId: migrateTargetFunnelId.value,
      targetStageId: migrateTargetStageId.value,
    });
    showMigrateModal.value = false;
    useAlert(t('CRM.DETAIL.MIGRATE_SUCCESS'));
    await router.push({
      name: 'crm_card_detail',
      params: {
        accountId: accountId.value,
        funnelId: data.funnel_id,
        cardId: cardId.value,
      },
    });
    await loadData();
  } catch (error) {
    useAlert(error?.response?.data?.error || t('CRM.DETAIL.MIGRATE_ERROR'));
  } finally {
    isMigrating.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col flex-1 h-full overflow-hidden bg-n-background">
    <!-- Header -->
    <header
      class="flex items-center gap-3 px-4 lg:px-6 py-3 border-b border-n-weak bg-n-background"
    >
      <button
        class="size-8 grid place-content-center rounded-lg hover:bg-n-alpha-2 transition-colors flex-shrink-0"
        @click="goBack"
      >
        <span class="i-lucide-arrow-left size-5 text-n-slate-11" />
      </button>
      <div v-if="card" class="flex items-center gap-2 min-w-0 flex-wrap">
        <h1
          class="text-lg lg:text-xl font-bold text-n-slate-12 truncate max-w-[50vw] lg:max-w-none"
        >
          {{ card.title }}
        </h1>
        <div class="flex items-center gap-2">
          <span
            class="px-2.5 py-0.5 text-xs font-medium rounded-full whitespace-nowrap"
            :class="statusBadge.class"
          >
            {{ statusBadge.label }}
          </span>
          <select
            v-if="funnelStages.length > 0"
            :value="card.funnel_stage_id"
            :disabled="isChangingStage"
            class="appearance-none inline-flex items-center h-5 leading-5 px-2.5 py-0 m-0 text-xs font-medium rounded-full border border-n-weak text-n-slate-11 whitespace-nowrap bg-transparent cursor-pointer hover:border-n-slate-7 focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed align-middle"
            :style="
              card.stage_color
                ? { borderColor: card.stage_color, color: card.stage_color }
                : {}
            "
            :title="t('CRM.DETAIL.STAGE_CHANGE_TOOLTIP')"
            @change="handleStageChange"
          >
            <option
              v-for="stage in funnelStages"
              :key="stage.id"
              :value="stage.id"
            >
              {{ stage.name }}
            </option>
          </select>
          <span
            v-else-if="card.stage_name"
            class="px-2.5 py-0.5 text-xs font-medium rounded-full border border-n-weak text-n-slate-11 whitespace-nowrap"
            :style="
              card.stage_color
                ? { borderColor: card.stage_color, color: card.stage_color }
                : {}
            "
          >
            {{ card.stage_name }}
          </span>
        </div>
      </div>
      <div v-if="card" class="ml-auto flex-shrink-0">
        <Button
          faded
          slate
          size="sm"
          icon="i-lucide-folder-input"
          :label="t('CRM.DETAIL.MIGRATE_BUTTON')"
          @click="openMigrateModal"
        />
      </div>
    </header>

    <!-- Loading skeleton -->
    <div
      v-if="uiFlags.isFetchingCard"
      class="flex flex-col lg:flex-row flex-1 overflow-y-auto lg:overflow-hidden"
    >
      <aside
        class="w-full lg:w-80 lg:flex-shrink-0 border-b lg:border-b-0 lg:border-r border-n-weak p-4"
      >
        <div class="flex flex-col gap-4">
          <div class="h-20 bg-n-slate-3 animate-pulse rounded-xl" />
          <div class="h-16 bg-n-slate-3 animate-pulse rounded-xl" />
          <div class="h-16 bg-n-slate-3 animate-pulse rounded-xl" />
        </div>
      </aside>
      <main class="flex-1 p-4">
        <div class="h-40 bg-n-slate-3 animate-pulse rounded-xl mb-4" />
        <div class="h-24 bg-n-slate-3 animate-pulse rounded-xl" />
      </main>
      <aside
        class="w-full lg:flex-1 border-t lg:border-t-0 lg:border-l border-n-weak p-4"
      >
        <div class="h-32 bg-n-slate-3 animate-pulse rounded-xl" />
      </aside>
    </div>

    <!-- Error state -->
    <div
      v-else-if="loadError"
      class="flex flex-col items-center justify-center flex-1 gap-3"
    >
      <div
        class="size-14 rounded-full bg-red-100 dark:bg-red-900/30 grid place-content-center"
      >
        <span
          class="i-lucide-alert-triangle size-7 text-red-600 dark:text-red-400"
        />
      </div>
      <span class="text-sm text-n-slate-11">
        {{ t('CRM.DETAIL.LOAD_ERROR') }}
      </span>
      <Button
        size="sm"
        icon="i-lucide-refresh-cw"
        :label="t('CRM.DETAIL.RETRY')"
        @click="loadData"
      />
    </div>

    <!-- 3-column layout -->
    <div
      v-else-if="card"
      class="flex flex-col lg:flex-row flex-1 overflow-y-auto lg:overflow-hidden"
    >
      <!-- Left Column - Deal Info -->
      <aside
        class="w-full lg:w-80 lg:flex-shrink-0 border-b lg:border-b-0 lg:border-r border-n-weak lg:overflow-y-auto p-4"
      >
        <div class="flex flex-col gap-3">
          <!-- Value Card -->
          <div class="bg-n-surface-1 rounded-xl border border-n-weak p-4">
            <div class="flex items-center gap-2 mb-2">
              <div
                class="size-7 rounded-full bg-green-100 dark:bg-green-900/30 grid place-content-center"
              >
                <span
                  class="i-lucide-dollar-sign size-4 text-green-600 dark:text-green-400"
                />
              </div>
              <span class="text-xs font-medium text-n-slate-10">
                {{ t('CRM.DETAIL.VALUE') }}
              </span>
            </div>
            <span class="text-2xl font-bold text-green-600 dark:text-green-400">
              {{ formattedValue }}
            </span>
          </div>

          <!-- Details Card -->
          <div class="bg-n-surface-1 rounded-xl border border-n-weak p-4">
            <div class="flex flex-col gap-3">
              <!-- Expected Close Date -->
              <div class="flex items-center gap-3">
                <div
                  class="size-7 rounded-full bg-n-alpha-2 grid place-content-center flex-shrink-0"
                >
                  <span class="i-lucide-calendar size-4 text-n-slate-10" />
                </div>
                <div class="min-w-0">
                  <span class="text-[11px] text-n-slate-9">
                    {{ t('CRM.DETAIL.EXPECTED_CLOSE') }}
                  </span>
                  <div class="text-sm text-n-slate-12">
                    {{
                      card.expected_close_date || t('CRM.DETAIL.NOT_DEFINED')
                    }}
                  </div>
                </div>
              </div>

              <!-- Responsible -->
              <div class="flex items-center gap-3">
                <div
                  class="size-7 rounded-full bg-n-alpha-2 grid place-content-center flex-shrink-0"
                >
                  <span class="i-lucide-user size-4 text-n-slate-10" />
                </div>
                <div class="flex items-center gap-2 min-w-0">
                  <Avatar
                    :name="card.created_by?.name"
                    :size="20"
                    rounded-full
                  />
                  <span class="text-sm text-n-slate-12 truncate">
                    {{ card.created_by?.name }}
                  </span>
                </div>
              </div>

              <!-- Time in Stage -->
              <div class="flex items-center gap-3">
                <div
                  class="size-7 rounded-full bg-n-alpha-2 grid place-content-center flex-shrink-0"
                >
                  <span class="i-lucide-clock size-4 text-n-slate-10" />
                </div>
                <div class="min-w-0">
                  <span class="text-[11px] text-n-slate-9">
                    {{ t('CRM.DETAIL.DAYS_IN_STAGE') }}
                  </span>
                  <div class="text-sm font-medium" :class="daysInStageColor">
                    {{ card.days_in_stage }} {{ t('CRM.DETAIL.DAYS') }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Loss Reason Warning -->
          <div
            v-if="card.status === 'lost' && card.loss_reason"
            class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4"
          >
            <div class="flex items-start gap-2">
              <span
                class="i-lucide-alert-triangle size-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5"
              />
              <div>
                <span
                  class="text-xs font-medium text-red-600 dark:text-red-400"
                >
                  {{ t('CRM.DETAIL.LOSS_REASON_TITLE') }}
                </span>
                <p class="text-sm text-red-700 dark:text-red-300 mt-0.5">
                  {{ card.loss_reason }}
                </p>
              </div>
            </div>
          </div>

          <!-- Action Buttons -->
          <div v-if="card.status === 'open'" class="grid grid-cols-2 gap-2">
            <button
              class="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium text-white bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600 transition-colors shadow-sm"
              @click="markWon"
            >
              <span class="i-lucide-trophy size-4 flex-shrink-0" />
              <span class="truncate">{{ t('CRM.DETAIL.MARK_WON') }}</span>
            </button>
            <button
              class="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600 transition-colors shadow-sm"
              @click="showLossModal = true"
            >
              <span class="i-lucide-x-circle size-4 flex-shrink-0" />
              <span class="truncate">{{ t('CRM.DETAIL.MARK_LOST') }}</span>
            </button>
          </div>
        </div>
      </aside>

      <!-- Center Column - Stage History -->
      <main
        class="flex-1 lg:overflow-y-auto p-4 border-b lg:border-b-0 border-n-weak"
      >
        <h3
          class="text-sm font-semibold text-n-slate-12 mb-3 flex items-center gap-2"
        >
          <span class="i-lucide-history size-4 text-n-slate-10" />
          {{ t('CRM.DETAIL.TIMELINE') }}
        </h3>
        <div v-if="uiFlags.isFetchingActivities" class="flex flex-col gap-3">
          <div
            v-for="i in 3"
            :key="i"
            class="h-16 bg-n-slate-3 animate-pulse rounded-xl"
          />
        </div>
        <div
          v-else-if="stageActivities.length === 0"
          class="flex flex-col items-center py-12 gap-2"
        >
          <div
            class="size-12 rounded-full bg-n-alpha-2 grid place-content-center"
          >
            <span class="i-lucide-activity size-6 text-n-slate-9" />
          </div>
          <span class="text-sm text-n-slate-10">
            {{ t('CRM.DETAIL.TIMELINE_EMPTY') }}
          </span>
        </div>
        <div v-else class="flex flex-col gap-3">
          <div
            v-for="activity in stageActivities"
            :key="activity.id"
            class="flex gap-3 bg-n-surface-1 rounded-xl border border-n-weak p-4 shadow-sm"
          >
            <div
              class="size-10 flex-shrink-0 rounded-full grid place-content-center"
              :class="
                (
                  activityIconMap[activity.activity_type] ||
                  activityIconMap.stage_change
                ).bg
              "
            >
              <span
                class="size-5"
                :class="[
                  (
                    activityIconMap[activity.activity_type] ||
                    activityIconMap.stage_change
                  ).icon,
                  (
                    activityIconMap[activity.activity_type] ||
                    activityIconMap.stage_change
                  ).text,
                ]"
              />
            </div>
            <div class="flex-1 min-w-0">
              <span class="text-sm font-medium text-n-slate-12">
                {{ activity.content }}
              </span>
              <div v-if="activity.meta" class="flex items-center gap-2 mt-1.5">
                <span
                  class="px-2 py-0.5 text-xs font-medium rounded-md bg-n-alpha-2 text-n-slate-11"
                >
                  {{ activity.meta.from_stage }}
                </span>
                <span class="i-lucide-arrow-right size-3.5 text-n-slate-9" />
                <span
                  class="px-2 py-0.5 text-xs font-medium rounded-md bg-n-alpha-2 text-n-slate-11"
                >
                  {{ activity.meta.to_stage }}
                </span>
              </div>
              <div
                class="flex items-center gap-2 mt-1.5 text-xs text-n-slate-10"
              >
                <span>{{ activity.created_by_name }}</span>
                <span>&middot;</span>
                <span>{{ relativeTime(activity.created_at) }}</span>
              </div>
            </div>
          </div>
        </div>
      </main>

      <!-- Right Column -->
      <aside
        class="w-full lg:flex-1 border-t lg:border-t-0 lg:border-l border-n-weak lg:overflow-y-auto p-4"
      >
        <div class="flex flex-col gap-3">
          <!-- Contact -->
          <div
            class="bg-n-surface-1 rounded-xl border border-n-weak p-4 shadow-sm"
          >
            <h3
              class="text-sm font-semibold text-n-slate-12 mb-3 flex items-center gap-2"
            >
              <span class="i-lucide-contact size-4 text-n-slate-10" />
              {{ t('CRM.DETAIL.CONTACT') }}
            </h3>
            <div v-if="card.contact" class="flex items-center gap-3">
              <Avatar
                :name="card.contact.name"
                :src="card.contact.thumbnail"
                :size="40"
                rounded-full
              />
              <div class="min-w-0">
                <div class="text-sm font-medium text-n-slate-12 truncate">
                  {{ card.contact.name }}
                </div>
                <div
                  v-if="card.contact.email"
                  class="text-xs text-n-slate-10 truncate"
                >
                  {{ card.contact.email }}
                </div>
                <div
                  v-if="card.contact.phone_number"
                  class="text-xs text-n-slate-10 truncate"
                >
                  {{ card.contact.phone_number }}
                </div>
              </div>
            </div>
            <router-link
              v-if="card.contact && contactConversationUrl"
              :to="contactConversationUrl"
              class="mt-3 flex items-center justify-center gap-2 w-full rounded-lg bg-woot-500 px-3 py-2 text-sm font-medium text-white hover:bg-woot-600 transition-colors"
            >
              <span class="i-lucide-message-circle size-4" />
              {{ t('CRM.DETAIL.GO_TO_CONVERSATION') }}
            </router-link>
            <div v-else class="flex flex-col items-center py-3 gap-1.5">
              <span class="i-lucide-user-x size-6 text-n-slate-7" />
              <span class="text-xs text-n-slate-10">
                {{ t('CRM.DETAIL.NO_CONTACT') }}
              </span>
            </div>
          </div>

          <!-- Timestamps -->
          <div
            class="bg-n-surface-1 rounded-xl border border-n-weak p-4 shadow-sm"
          >
            <div class="flex flex-col gap-3">
              <div>
                <span class="text-[11px] text-n-slate-9">
                  {{ t('CRM.DETAIL.CREATED_AT') }}
                </span>
                <div class="text-sm text-n-slate-12">
                  {{ formattedDate(card.created_at) }}
                </div>
              </div>
              <div>
                <span class="text-[11px] text-n-slate-9">
                  {{ t('CRM.DETAIL.UPDATED_AT') }}
                </span>
                <div class="text-sm text-n-slate-12">
                  {{ formattedDate(card.updated_at) }}
                </div>
              </div>
              <div v-if="card.won_at">
                <span class="text-[11px] text-green-600 dark:text-green-400">
                  {{ t('CRM.DETAIL.STATUS_WON') }}
                </span>
                <div class="text-sm text-green-700 dark:text-green-300">
                  {{ formattedDate(card.won_at) }}
                </div>
              </div>
              <div v-if="card.lost_at">
                <span class="text-[11px] text-red-600 dark:text-red-400">
                  {{ t('CRM.DETAIL.STATUS_LOST') }}
                </span>
                <div class="text-sm text-red-700 dark:text-red-300">
                  {{ formattedDate(card.lost_at) }}
                </div>
              </div>
            </div>
          </div>

          <!-- Activity Form -->
          <div
            class="bg-n-surface-1 rounded-xl border border-n-weak p-4 shadow-sm"
          >
            <h3
              class="text-sm font-semibold text-n-slate-12 mb-3 flex items-center gap-2"
            >
              <span class="i-lucide-plus-circle size-4 text-n-slate-10" />
              {{ t('CRM.DETAIL.ACTIVITY') }}
            </h3>
            <div class="flex flex-wrap gap-1.5 mb-3">
              <button
                v-for="aType in activityTypes"
                :key="aType.value"
                class="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-all duration-150"
                :class="
                  activityType === aType.value
                    ? 'text-white shadow-sm'
                    : 'bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3'
                "
                :style="
                  activityType === aType.value
                    ? { backgroundColor: aType.activeColor }
                    : {}
                "
                @click="activityType = aType.value"
              >
                <span :class="aType.icon" class="size-3" />
                {{ t(aType.labelKey) }}
              </button>
            </div>
            <!-- File upload UI -->
            <div v-if="activityType === 'file'" class="flex flex-col gap-2">
              <div
                class="flex items-center gap-2 rounded-lg border border-dashed border-n-weak bg-n-background p-3 cursor-pointer hover:border-n-blue-7 transition-colors"
                @click="fileInputRef?.click()"
              >
                <span
                  class="i-lucide-upload size-5 text-n-slate-10 flex-shrink-0"
                />
                <span v-if="!selectedFile" class="text-sm text-n-slate-10">
                  {{ t('CRM.DETAIL.ACTIVITY_FILE_SELECT') }}
                </span>
                <div v-else class="flex-1 min-w-0">
                  <span class="text-sm text-n-slate-12 truncate block">
                    {{ selectedFile.name }}
                  </span>
                  <span class="text-xs text-n-slate-9">
                    {{ formatFileSize(selectedFile.size) }}
                  </span>
                </div>
                <button
                  v-if="selectedFile"
                  class="size-6 grid place-content-center rounded hover:bg-n-ruby-2 transition-colors flex-shrink-0"
                  @click.stop="clearFile"
                >
                  <span class="i-lucide-x size-3.5 text-n-slate-10" />
                </button>
              </div>
              <input
                ref="fileInputRef"
                type="file"
                class="hidden"
                @change="onFileSelect"
              />
              <span class="text-[11px] text-n-slate-9">
                {{ t('CRM.DETAIL.ACTIVITY_FILE_LIMIT') }}
              </span>
              <textarea
                v-model="activityContent"
                rows="2"
                class="w-full rounded-lg border border-n-weak bg-n-background p-2.5 text-sm text-n-slate-12 placeholder:text-n-slate-9 resize-none focus:ring-2 focus:ring-n-blue-7 focus:border-n-blue-7 transition-colors"
                :placeholder="t('CRM.DETAIL.ACTIVITY_FILE_PLACEHOLDER')"
              />
            </div>
            <!-- Text activity input -->
            <textarea
              v-else
              v-model="activityContent"
              rows="3"
              class="w-full rounded-lg border border-n-weak bg-n-background p-2.5 text-sm text-n-slate-12 placeholder:text-n-slate-9 resize-none focus:ring-2 focus:ring-n-blue-7 focus:border-n-blue-7 transition-colors"
              :placeholder="t('CRM.DETAIL.ACTIVITY_CONTENT_PLACEHOLDER')"
            />
            <div class="flex justify-end mt-2">
              <Button
                :label="t('CRM.DETAIL.ACTIVITY_SAVE')"
                size="sm"
                :disabled="
                  activityType === 'file'
                    ? !selectedFile
                    : !activityContent.trim()
                "
                @click="submitActivity"
              />
            </div>
          </div>

          <!-- Activity Tabs -->
          <div
            class="bg-n-surface-1 rounded-xl border border-n-weak shadow-sm overflow-hidden"
          >
            <!-- Tab headers -->
            <div
              class="flex border-b border-n-weak overflow-x-auto scrollbar-hide"
            >
              <button
                v-for="aType in activityTypes"
                :key="aType.value"
                class="flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium whitespace-nowrap transition-colors border-b-2 -mb-px"
                :class="
                  activeTab === aType.value
                    ? 'border-current text-n-slate-12'
                    : 'border-transparent text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-1'
                "
                :style="
                  activeTab === aType.value ? { color: aType.activeColor } : {}
                "
                @click="activeTab = aType.value"
              >
                <span :class="aType.icon" class="size-3.5" />
                {{ t(aType.labelKey) }}
                <span
                  v-if="activityCountByType[aType.value]"
                  class="px-1.5 py-0.5 text-[10px] rounded-full bg-n-alpha-2"
                >
                  {{ activityCountByType[aType.value] }}
                </span>
              </button>
            </div>

            <!-- Tab content -->
            <div class="p-4">
              <!-- Merge files to PDF (only Files tab, only when there are mergeable files) -->
              <div
                v-if="activeTab === 'file' && hasMergeableFiles"
                class="flex justify-center mb-3"
              >
                <Button
                  size="sm"
                  icon="i-lucide-file-down"
                  :label="t('CRM.DETAIL.MERGE_PDF_BUTTON')"
                  @click="openMergeModal"
                />
              </div>
              <div
                v-if="activeTabItems.length === 0"
                class="flex flex-col items-center py-8 gap-2"
              >
                <div
                  class="size-10 rounded-full bg-n-alpha-2 grid place-content-center"
                >
                  <span
                    :class="activityIconMap[activeTab].icon"
                    class="size-5 text-n-slate-9"
                  />
                </div>
                <span class="text-sm text-n-slate-10">
                  {{ t('CRM.DETAIL.TIMELINE_EMPTY') }}
                </span>
              </div>
              <div v-else class="flex flex-col gap-2">
                <div
                  v-for="activity in activeTabItems"
                  :key="activity.id"
                  class="rounded-lg border border-n-weak p-3"
                >
                  <!-- Inline edit -->
                  <div v-if="editingActivityId === activity.id">
                    <textarea
                      v-model="editingContent"
                      rows="2"
                      class="w-full rounded-lg border border-n-blue-7 bg-n-background p-2 text-sm text-n-slate-12 resize-none focus:ring-2 focus:ring-n-blue-7 focus:outline-none"
                    />
                    <div class="flex gap-2 mt-1.5">
                      <Button
                        size="sm"
                        :label="t('CRM.DETAIL.ACTIVITY_SAVE')"
                        :disabled="!editingContent.trim()"
                        @click="saveEditActivity(activity)"
                      />
                      <Button
                        faded
                        slate
                        size="sm"
                        :label="t('CRM.DETAIL.ACTIVITY_CANCEL_EDIT')"
                        @click="cancelEditActivity"
                      />
                    </div>
                  </div>
                  <!-- Normal view -->
                  <div v-else>
                    <div class="flex items-start justify-between gap-2">
                      <div class="flex-1 min-w-0">
                        <!-- File attachment -->
                        <a
                          v-if="activity.file"
                          :href="activity.file.url"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="flex items-center gap-2 p-2 rounded-lg bg-n-alpha-2 hover:bg-n-alpha-3 transition-colors mb-1.5"
                        >
                          <span
                            class="i-lucide-file size-5 text-indigo-500 flex-shrink-0"
                          />
                          <div class="min-w-0 flex-1">
                            <span
                              class="text-sm text-n-slate-12 truncate block"
                            >
                              {{ displayFileName(activity) }}
                            </span>
                            <span class="text-[11px] text-n-slate-9">
                              {{ formatFileSize(activity.file.byte_size) }}
                            </span>
                          </div>
                          <span
                            class="i-lucide-download size-4 text-n-slate-10 flex-shrink-0"
                          />
                        </a>
                        <p
                          v-if="activity.content"
                          class="text-sm text-n-slate-12 whitespace-pre-wrap"
                        >
                          {{ activity.content }}
                        </p>
                      </div>
                      <div
                        v-if="isEditableActivity(activity)"
                        class="flex gap-0.5 flex-shrink-0"
                      >
                        <button
                          v-if="activity.activity_type !== 'file'"
                          class="size-6 grid place-content-center rounded hover:bg-n-alpha-2 transition-colors"
                          @click="startEditActivity(activity)"
                        >
                          <span
                            class="i-lucide-pencil size-3 text-n-slate-10"
                          />
                        </button>
                        <button
                          class="size-6 grid place-content-center rounded hover:bg-n-ruby-2 transition-colors"
                          @click="deleteActivity(activity)"
                        >
                          <span
                            class="i-lucide-trash-2 size-3 text-n-slate-10 hover:text-n-ruby-11"
                          />
                        </button>
                      </div>
                    </div>
                    <div
                      class="flex items-center gap-2 mt-1 text-xs text-n-slate-10"
                    >
                      <span>{{ activity.created_by_name }}</span>
                      <span>&middot;</span>
                      <span>{{ relativeTime(activity.created_at) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </div>

    <!-- Loss Reason Modal -->
    <woot-modal
      v-model:show="showLossModal"
      :on-close="() => (showLossModal = false)"
    >
      <div class="flex flex-col h-auto overflow-auto">
        <woot-modal-header :header-title="t('CRM.DETAIL.LOSS_REASON_TITLE')" />
        <form class="w-full" @submit.prevent="markLost">
          <div class="w-full">
            <label>
              {{ t('CRM.DETAIL.LOSS_REASON_TITLE') }}
              <textarea
                v-model="lossReason"
                rows="3"
                :placeholder="t('CRM.DETAIL.LOSS_REASON_PLACEHOLDER')"
              />
            </label>
          </div>
          <div class="flex justify-end gap-2 py-2">
            <Button
              faded
              slate
              type="button"
              :label="t('CRM.DETAIL.LOSS_REASON_CANCEL')"
              @click="showLossModal = false"
            />
            <Button
              type="submit"
              :label="t('CRM.DETAIL.LOSS_REASON_CONFIRM')"
              class="bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600 text-white"
            />
          </div>
        </form>
      </div>
    </woot-modal>

    <!-- Migrate to another funnel modal -->
    <woot-modal
      v-model:show="showMigrateModal"
      :on-close="() => (showMigrateModal = false)"
    >
      <div class="flex flex-col h-auto overflow-auto p-1">
        <woot-modal-header
          :header-title="t('CRM.DETAIL.MIGRATE_TITLE')"
          :header-content="t('CRM.DETAIL.MIGRATE_DESCRIPTION')"
        />
        <div class="flex flex-col gap-4 w-full p-2">
          <div
            v-if="eligibleFunnels.length === 0"
            class="text-sm text-n-slate-11"
          >
            {{ t('CRM.DETAIL.MIGRATE_NO_FUNNEL') }}
          </div>
          <template v-else>
            <label
              class="flex flex-col gap-1 text-sm font-medium text-n-slate-12"
            >
              {{ t('CRM.DETAIL.MIGRATE_TARGET_FUNNEL') }}
              <select
                v-model="migrateTargetFunnelId"
                class="text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-background"
              >
                <option :value="null">
                  {{ t('CRM.DETAIL.MIGRATE_SELECT_FUNNEL') }}
                </option>
                <option v-for="f in eligibleFunnels" :key="f.id" :value="f.id">
                  {{ f.name }}
                </option>
              </select>
            </label>

            <label
              v-if="migrateTargetFunnelId"
              class="flex flex-col gap-1 text-sm font-medium text-n-slate-12"
            >
              {{ t('CRM.DETAIL.MIGRATE_TARGET_STAGE') }}
              <select
                v-model="migrateTargetStageId"
                :disabled="
                  isLoadingMigrateStages || migrateTargetStages.length === 0
                "
                class="text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-background disabled:opacity-60"
              >
                <option
                  v-for="s in migrateTargetStages"
                  :key="s.id"
                  :value="s.id"
                >
                  {{ s.name }}
                </option>
              </select>
            </label>

            <div
              class="bg-n-amber-3 border border-n-amber-6 rounded-md p-3 text-sm text-n-slate-12"
            >
              {{ t('CRM.DETAIL.MIGRATE_HINT') }}
            </div>
          </template>
        </div>
        <div class="flex justify-end gap-2 py-2 px-2">
          <Button
            faded
            slate
            type="button"
            :label="t('CRM.DETAIL.MIGRATE_CANCEL')"
            :disabled="isMigrating"
            @click="showMigrateModal = false"
          />
          <Button
            type="button"
            :label="t('CRM.DETAIL.MIGRATE_CONFIRM')"
            :is-loading="isMigrating"
            :disabled="
              !migrateTargetFunnelId || !migrateTargetStageId || isMigrating
            "
            @click="performMigrate"
          />
        </div>
      </div>
    </woot-modal>

    <!-- Merge files to PDF modal -->
    <woot-modal
      v-model:show="showMergeModal"
      :on-close="() => (showMergeModal = false)"
    >
      <div class="flex flex-col h-auto overflow-auto p-1">
        <woot-modal-header
          :header-title="t('CRM.DETAIL.MERGE_PDF_MODAL_TITLE')"
          :header-content="t('CRM.DETAIL.MERGE_PDF_MODAL_DESCRIPTION')"
        />
        <div class="flex flex-col gap-1 w-full p-2">
          <label
            class="flex items-center gap-2 text-sm font-medium text-n-slate-12 pb-2 mb-1 border-b border-n-weak cursor-pointer"
          >
            <input v-model="allMergeSelected" type="checkbox" class="size-4" />
            {{ t('CRM.DETAIL.MERGE_PDF_SELECT_ALL') }}
          </label>
          <label
            v-for="activity in mergeableFileActivities"
            :key="activity.id"
            class="flex items-center gap-2 text-sm text-n-slate-12 py-1.5 px-1 rounded hover:bg-n-alpha-2 cursor-pointer"
          >
            <input
              v-model="selectedMergeIds"
              type="checkbox"
              :value="activity.id"
              class="size-4 flex-shrink-0"
            />
            <span class="i-lucide-file size-4 text-indigo-500 flex-shrink-0" />
            <span class="truncate flex-1">{{ displayFileName(activity) }}</span>
            <span class="text-[11px] text-n-slate-9 flex-shrink-0">
              {{ formatFileSize(activity.file.byte_size) }}
            </span>
          </label>
        </div>
        <div class="flex justify-end gap-2 py-2 px-2">
          <Button
            faded
            slate
            type="button"
            :label="t('CRM.DETAIL.MERGE_PDF_CANCEL')"
            :disabled="isMergingPdf"
            @click="showMergeModal = false"
          />
          <Button
            type="button"
            icon="i-lucide-file-down"
            :label="
              isMergingPdf
                ? t('CRM.DETAIL.MERGE_PDF_LOADING')
                : t('CRM.DETAIL.MERGE_PDF_CONFIRM')
            "
            :is-loading="isMergingPdf"
            :disabled="selectedMergeIds.length === 0 || isMergingPdf"
            @click="confirmMergeFilesToPdf"
          />
        </div>
      </div>
    </woot-modal>
  </div>
</template>
